
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Shield, Users, Layers, Lock } from 'lucide-react';
// 🛰️ Using unified types to resolve property mismatches
import { KanbanTask, BoardConfig, TaskStatus, Role, DataScope } from '@/types';
import { TaskCard } from '../../../../src/views/TaskCard';

interface KanbanBoardProps {
  config: BoardConfig;
  tasks: KanbanTask[];
  onMoveTask: (taskId: string, newStatus: TaskStatus, destinationBoard?: BoardConfig) => void; // Adjusted signature for consistency
  // 🛡️ Hybrid Scoping Props
  currentUserId: string;
  currentHouseholdId: string | null;
  userPermissions?: any; // Derived from household permission matrix
  onAddTask: () => void;
  onViewTask: (task: KanbanTask) => void;
  activeDataScope: DataScope; // 🛡️ NEW: Prop to receive active scope from parent
  onDataScopeChange: (scope: DataScope) => void; // 🛡️ NEW: Callback to update active scope
}

export const KanbanBoard: React.FC<KanbanBoardProps> = ({
  config,
  tasks,
  onMoveTask,
  onAddTask,
  onViewTask,
  currentUserId,
  currentHouseholdId,
  userPermissions,
  activeDataScope, // 🛡️ NEW: Destructure activeDataScope
  onDataScopeChange // 🛡️ NEW: Destructure onDataScopeChange
}) => {
  const { t } = useTranslation();

  /**
   * 🛡️ HYBRID FILTERING ENGINE
   * Respects privacy boundaries: ensures personal tasks from other members are never visible.
   */
  const filteredTasks = useMemo(() => {
    return (tasks || []).filter((t: KanbanTask) => {
      const isCorrectBoard = t.boardId === config.id && !t.isDeleted;
      
      let scopeMatches = true;
      if (activeDataScope === 'PERSONAL') scopeMatches = t.scope === 'personal';
      if (activeDataScope === 'SHARED') scopeMatches = t.scope === 'household';

      // 🔒 Privacy Guard: Never leak personal tasks of other users
      const privacyCheck = t.scope === 'personal' ? t.ownerId === currentUserId : true;

      return isCorrectBoard && scopeMatches && privacyCheck;
    });
  }, [tasks, config.id, activeDataScope, currentUserId]);

  const renderColumn = (status: TaskStatus) => {
    const columnTasks = filteredTasks.filter((t: KanbanTask) => t.status === status);
    
    const { setNodeRef, isOver } = useDroppable({
      id: `${config.id}-${status}`, 
      data: { board: config, status: status }
    });
    
    const getColumnTitle = (s: TaskStatus): string => {
      const titles: Record<string, string> = {
        todo: config.templateType === 'grocery' ? 'Needed' : 'To-Do',
        in_progress: config.templateType === 'grocery' ? 'In Cart' : 'Active',
        pending_approval: 'Needs Approval',
        pantry: 'Pantry',
        finalized: 'Finalized',
        history: 'Registry'
      };
      return titles[s as string] || (s as string).toUpperCase();
    };

    return (
      <div
        ref={setNodeRef}
        key={status}
        className={`flex-1 min-w-[320px] rounded-[2rem] p-5 flex flex-col h-full border-2 transition-all duration-300 ${
          isOver
            ? 'bg-brand/5 dark:bg-brand/10 border-brand shadow-brand-lg'
            : 'bg-brand/[0.04] dark:bg-brand/[0.06] border-brand/20 dark:border-brand/15'
        }`}
      >
        <div className="flex justify-between items-center mb-6 px-3">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${isOver ? 'bg-brand animate-ping' : 'bg-brand/40'}`} />
            <h2 className="font-black text-2xs text-brand uppercase tracking-[0.3em]">
              {getColumnTitle(status)}
            </h2>
          </div>
          <span className="bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-2xs px-3 py-1 rounded-xl font-black shadow-sm border border-slate-100 dark:border-slate-700">
            {columnTasks.length}
          </span>
        </div>

        <div className="flex-1 overflow-y-auto space-y-4 no-scrollbar">
          <SortableContext 
            items={columnTasks.map((t: KanbanTask) => t.id)} 
            strategy={verticalListSortingStrategy}
          >
            {columnTasks.map((task: KanbanTask) => (
              <TaskCard 
                key={task.id} 
                task={task} 
                onMove={(id, nextStatus) => onMoveTask(id, nextStatus)} // Removed task.scope from here
                onClick={onViewTask}
              />
            ))}
          </SortableContext>
          
          {columnTasks.length === 0 && (
            <div className={`border-2 border-dashed rounded-3xl h-32 flex flex-col items-center justify-center gap-2 transition-all ${
              isOver ? 'border-indigo-300 bg-indigo-50/50' : 'border-slate-100 dark:border-slate-800 opacity-50'
            }`}>
              <p className="text-2xs font-black uppercase text-slate-400 tracking-widest italic">
                {isOver ? t('kanban.drop_here', 'Release to Move') : t('kanban.empty_column', 'Nothing here')}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col w-full h-full space-y-6">
      {/* --- BOARD HEADER --- */}
      <div className="flex flex-col md:flex-row md:items-center justify-between px-4 gap-4">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-black text-slate-900 dark:text-white uppercase italic tracking-tighter">
            {config.templateType === 'grocery' ? '🛒' : '⚡'} {config.title}
          </h1>
          {config.visibility === 'admin' && (
             <span className="text-[9px] bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-500 px-3 py-1 rounded-full font-black uppercase tracking-widest border border-amber-100 dark:border-amber-800">
               Privileged Access
             </span>
          )}
        </div>

      </div>

      {/* --- HORIZONTAL COLUMN CONTAINER --- */}
      <div className="flex gap-6 overflow-x-auto pb-8 h-[calc(100vh-300px)] items-start no-scrollbar px-2">
        {config.allowedStatuses.map((status: TaskStatus) => renderColumn(status))}
      </div>
    </div>
  );
};